import React, { memo } from 'react';

import {
  CardContent,
  Grid,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { Card } from '@mui/material';
import { UiTooltip, TOOLTIPS_TEXTS } from '@mais-retorno-front/libs/ui-tooltip';
import {
  formatCnpj,
  formatNumber,
  getUtcDate,
} from '@mais-retorno-front/libs/util-helpers';

import { Description, TooltipLink } from './styles';
import { ISheetFundHeaderProps } from './model';
import { FundStats } from './components/fundStats';
import { RenderTextOrLink } from './components/renderTextOrLink';
import { renderTextOrNonValue } from './helper';

import { AiOutlinePercentage } from '@react-icons/all-files/ai/AiOutlinePercentage';
import { FiDollarSign } from '@react-icons/all-files/fi/FiDollarSign';
import { BiTrendingUp } from '@react-icons/all-files/bi/BiTrendingUp';
import { FiActivity } from '@react-icons/all-files/fi/FiActivity';
import { BsBarChartFill } from '@react-icons/all-files/bs/BsBarChartFill';
import { FiUsers } from '@react-icons/all-files/fi/FiUsers';

export const UiSheetFundHeader = memo<ISheetFundHeaderProps>(
  ({
    fundHeader,
    fundStats,
    useLink = false,
    loadingStats = true,
    routesLink,
    Link,
    headerStyle = {},
  }: ISheetFundHeaderProps) => {
    const theme = useTheme();

    const isMobile = useMediaQuery<Theme>((theme) =>
      theme.breakpoints.only('xs')
    );

    return (
      <>
        <div style={{ marginTop: theme.spacing(4) }}>
          {fundHeader && (
            <Card sx={headerStyle}>
              <CardContent>
                <Grid container spacing={3} component="ul" sx={{ p: 0 }}>
                  <Grid item xs={12} sm={6} md={4} component="li">
                    <Typography
                      variant="body1"
                      component="h2"
                      style={{ margin: 0, fontWeight: 700 }}
                    >
                      Razão Social
                      <Description id="fund-name" data-testid="fund-name">
                        {renderTextOrNonValue(fundHeader?.name)}
                      </Description>
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2} component="li">
                    <Typography
                      variant="body1"
                      component="h3"
                      style={{ margin: 0, fontWeight: 700 }}
                    >
                      CNPJ
                      <Description data-testid="fund-cnpj">
                        {renderTextOrNonValue(
                          formatCnpj(fundHeader?.cnpj?.toString())
                        )}
                      </Description>
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6} md={2} component="li">
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        variant="body1"
                        component="span"
                        style={{ margin: 0, fontWeight: 700 }}
                      >
                        Benchmark
                      </Typography>
                      <UiTooltip
                        text={
                          <span>
                            {TOOLTIPS_TEXTS.benchmark}
                            <TooltipLink
                              href="//maisretorno.com/portal/termos/b/benchmark"
                              target="_blank"
                            >
                              Benchmark
                            </TooltipLink>
                          </span>
                        }
                      />
                    </div>
                    <Description data-testid="fund-benhcmark">
                      {renderTextOrNonValue(
                        fundHeader?.cvm_property?.benchmark,
                        fundHeader?.cvm_property?.benchmark
                      )}
                    </Description>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2} component="li">
                    <Typography
                      variant="body1"
                      component="span"
                      style={{ margin: 0, fontWeight: 700 }}
                    >
                      Data inicial
                    </Typography>
                    <Description data-testid="fund-initial-date">
                      {renderTextOrNonValue(
                        fundHeader?.first_quote_date,
                        getUtcDate(fundHeader?.first_quote_date, 'DD/MM/YYYY')
                      )}
                    </Description>
                  </Grid>
                  <Grid item xs={12} sm={6} md={2} component="li">
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Typography
                        variant="body1"
                        component="span"
                        style={{ margin: 0, fontWeight: 700 }}
                      >
                        Tipo do fundo
                      </Typography>
                      {fundHeader?.cvm_property?.fund_type && (
                        <UiTooltip
                          text={
                            TOOLTIPS_TEXTS[fundHeader.cvm_property.fund_type]
                          }
                        />
                      )}
                    </div>
                    <Description data-testid="fund-type">
                      {renderTextOrNonValue(
                        fundHeader?.cvm_property?.fund_type
                      )}
                    </Description>
                  </Grid>
                  <Grid item xs={12} sm={6} md={4} component="li">
                    <Typography
                      variant="body1"
                      component="h3"
                      style={{ margin: 0, fontWeight: 700 }}
                    >
                      Administrador
                    </Typography>

                    <RenderTextOrLink
                      useLink={useLink}
                      text={fundHeader?.admin?.nicename}
                      link={`${routesLink.adminListFunds}/${fundHeader?.admin?.slug}`}
                      Link={Link}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2} component="li">
                    <Typography
                      variant="body1"
                      component="p"
                      style={{ margin: 0, fontWeight: 700 }}
                    >
                      Classe
                    </Typography>
                    <RenderTextOrLink
                      useLink={fundHeader?.class?.slug && useLink}
                      text={fundHeader?.class?.name}
                      link={`${routesLink.rankingFund}/${fundHeader?.class?.slug}`}
                      Link={Link}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={2} component="li">
                    <Typography
                      variant="body1"
                      component="p"
                      style={{ margin: 0, fontWeight: 700 }}
                    >
                      Subclasse
                    </Typography>

                    <RenderTextOrLink
                      useLink={
                        fundHeader?.class?.slug &&
                        fundHeader?.subclass?.slug &&
                        useLink
                      }
                      text={fundHeader?.subclass?.name}
                      link={`${routesLink.rankingFund}/${fundHeader?.subclass?.slug}`}
                      Link={Link}
                    />
                  </Grid>
                  <Grid item xs={12} component="li">
                    <Typography
                      variant="body1"
                      component="h3"
                      style={{ margin: 0, fontWeight: 700 }}
                    >
                      {fundHeader?.asset_managers?.length > 1
                        ? 'Gestores'
                        : 'Gestor'}
                    </Typography>
                    <Stack flexDirection="row" gap={1.5} flexWrap="wrap">
                      {fundHeader?.asset_managers?.map((manager) => (
                        <RenderTextOrLink
                          key={manager?.slug}
                          useLink={useLink}
                          text={manager?.nicename}
                          link={`${routesLink.managerListFund}/${manager?.slug}`}
                          Link={Link}
                        />
                      ))}
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          )}

          {fundStats && (
            <section
              style={{ marginTop: theme.spacing(4), breakInside: 'avoid' }}
            >
              <Grid
                container
                role="table"
                alignItems={isMobile ? 'center' : 'flex-start'}
                spacing={1}
              >
                <FundStats
                  title="Rentabilidade 12M"
                  loading={loadingStats}
                  value={formatNumber(
                    fundStats?.stats?.timeframe?.last_12_months.profitability,
                    { type: 'percent' }
                  )}
                  negativeValue={
                    fundStats?.stats?.timeframe?.last_12_months.profitability <
                    0
                  }
                  icon={<AiOutlinePercentage size={18} />}
                />
                <FundStats
                  loading={loadingStats}
                  title="Patrimônio Líquido"
                  value={formatNumber(fundStats?.stats?.networth, {
                    type: 'money',
                    thousandMask: true,
                  })}
                  negativeValue={fundStats?.stats?.networth < 0}
                  icon={<FiDollarSign size={18} />}
                />
                <FundStats
                  loading={loadingStats}
                  title="PL Médio 12M"
                  value={formatNumber(
                    fundStats?.stats?.['12_months_average_networth'],
                    {
                      type: 'money',
                      thousandMask: true,
                    }
                  )}
                  negativeValue={
                    fundStats?.stats?.['12_months_average_networth'] < 0
                  }
                  icon={<BiTrendingUp size={18} />}
                />
                <FundStats
                  loading={loadingStats}
                  title="Volatilidade 12M"
                  value={formatNumber(
                    fundStats?.stats?.timeframe?.last_12_months?.volatility,
                    { type: 'percent' }
                  )}
                  negativeValue={
                    fundStats?.stats?.timeframe?.last_12_months?.volatility < 0
                  }
                  icon={<FiActivity size={18} />}
                />

                <FundStats
                  loading={loadingStats}
                  title="Índice de Sharpe 12M"
                  value={formatNumber(
                    fundStats?.stats?.timeframe?.last_12_months?.sharpe_ratio
                  )}
                  negativeValue={
                    fundStats?.stats?.timeframe?.last_12_months?.sharpe_ratio <
                    0
                  }
                  icon={<BsBarChartFill size={18} />}
                />
                <FundStats
                  loading={loadingStats}
                  title="Cotistas"
                  value={formatNumber(fundStats?.stats?.quota_holders, {
                    thousandMask: true,
                    precision: 0,
                  })}
                  negativeValue={fundStats?.stats?.quota_holders < 0}
                  icon={<FiUsers size={18} />}
                />
              </Grid>
            </section>
          )}
        </div>
      </>
    );
  }
);
